import * as React from 'react';
import { Eye, EyeOff } from 'lucide-react';

import { cn } from '../utils/utils';
import { Input } from './input';

/**
 * A password field with a reveal toggle.
 *
 * Every current login/signup UX source names "no password visibility toggle"
 * among the handful of failures that measurably cost conversions, and it is
 * also what lets a sign-up form drop its confirm-password field: the user
 * verifies by looking rather than by typing the same string twice.
 *
 * Forwards its ref and spreads the rest of its props onto the `<input>`, so it
 * drops straight into a react-hook-form `<FormControl>` in place of `<Input
 * type='password'>`.
 *
 * `aria-controls` points at the input so the relationship is exposed, and
 * `aria-pressed` carries the toggle's own state. The button is deliberately
 * NOT `aria-hidden` - a sighted keyboard user needs to reach it.
 */
export interface PasswordInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Extra classes for the wrapper, not the input. */
  wrapperClassName?: string;
}

const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ className, wrapperClassName, id, ...props }, ref) => {
    const [shown, setShown] = React.useState(false);
    const generated = React.useId();
    const inputId = id ?? generated;

    return (
      <div className={cn('relative', wrapperClassName)}>
        <Input
          id={inputId}
          ref={ref}
          type={shown ? 'text' : 'password'}
          className={cn('pr-11', className)}
          {...props}
        />
        <button
          type='button'
          onClick={() => setShown(s => !s)}
          aria-controls={inputId}
          aria-pressed={shown}
          aria-label={shown ? 'Hide password' : 'Show password'}
          // The full height of the field and 44px wide, flush to its right
          // edge: on the 44px auth fields that is a 44x44 tap target, where the
          // old `inset-y-1 right-1 px-2` box was 32x36 - small for a thumb,
          // right beside the field it would otherwise focus. The icon is
          // centred in it, so it is drawn within 2px of where it was. The
          // input's `pr-11` keeps typed text clear of the whole button.
          className='text-muted-foreground hover:text-foreground focus-visible:ring-ring absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-md transition-colors focus-visible:ring-1 focus-visible:outline-none'
        >
          {shown ? <EyeOff className='h-4 w-4' /> : <Eye className='h-4 w-4' />}
        </button>
      </div>
    );
  }
);
PasswordInput.displayName = 'PasswordInput';

export { PasswordInput };
